/**
 * UserPrefsSync
 *
 * Sits inside AuthProvider. When the authenticated user changes,
 * reads their saved theme + lang from Supabase user_metadata and applies them.
 *
 * This makes preferences follow the user across devices and browsers —
 * each user has their own independent settings.
 */
import { useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useTheme } from '../contexts/ThemeContext'
import { useLang } from '../contexts/LangContext'
import type { Theme } from '../contexts/ThemeContext'
import type { Lang } from '../contexts/LangContext'

const VALID_THEMES: Theme[] = ['light', 'dark', 'system']
const VALID_LANGS:  Lang[]  = ['pt', 'en', 'es']

export function UserPrefsSync() {
  const { applyFromMetadata: applyTheme } = useTheme()
  const { applyFromMetadata: applyLang }  = useLang()
  const lastUserId = useRef<string | null>(null)

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        const user = session?.user ?? null

        // Only sync when a NEW user logs in (not on every token refresh)
        if (!user || user.id === lastUserId.current) return
        lastUserId.current = user.id

        const meta = user.user_metadata ?? {}

        // Always apply — fall back to defaults so a new user never inherits
        // leftover localStorage values from a previous user's session.
        const theme = (meta.theme && VALID_THEMES.includes(meta.theme as Theme))
          ? meta.theme as Theme
          : 'system'
        const lang = (meta.lang && VALID_LANGS.includes(meta.lang as Lang))
          ? meta.lang as Lang
          : 'pt'

        applyTheme(theme)
        applyLang(lang)
      }
    )

    return () => subscription.unsubscribe()
  }, [applyTheme, applyLang])

  return null
}
